/**
 * Seed data for the business-policies mock API.
 *
 * Contains the 8 foundational policies from ADR-103 Appendix A plus a
 * handful of recent evaluation rows per policy for the Evaluations tab.
 * These are the exact policies referenced in the CTO handoff and act as
 * the canonical fixture for the PolicyEditor + ConfigSimulator screens.
 */
import type {
  PolicyDocument,
  PolicyEvaluationRecord,
  PolicyRecord,
  PolicyTenantOverride,
} from './types';

const NOW = Date.now();
const iso = (offsetMs: number) => new Date(NOW + offsetMs).toISOString();
const HOURS = 60 * 60 * 1000;
const DAYS = 24 * HOURS;

function buildRecord(
  id: string,
  doc: PolicyDocument,
  updates: {
    status?: PolicyRecord['status'];
    activatedAt?: string;
    deprecatedAt?: string;
    updatedAt?: string;
  } = {},
): PolicyRecord {
  return {
    id,
    policyId: doc.policyId,
    version: doc.version,
    name: doc.name,
    description: doc.description,
    status: updates.status ?? doc.status,
    scopeActions: doc.scope.actions,
    scopeVerticalKeys: doc.scope.subVerticalKeys,
    priority: doc.priority,
    combinator: doc.combinator,
    audit: doc.audit,
    onDeny: doc.onDeny,
    document: doc,
    createdAt: iso(-30 * DAYS),
    updatedAt: updates.updatedAt ?? iso(-5 * DAYS),
    activatedAt: updates.activatedAt,
    deprecatedAt: updates.deprecatedAt,
    checksumSha256: `sha256-${id}-mock`,
  };
}

/**
 * 1. POL-VET-001 — vet license required
 */
const POL_VET_001: PolicyDocument = {
  policyId: 'POL-VET-001',
  version: '1.0.0',
  name: 'Veteriner lisansı doğrulaması',
  description: 'Veterinerlerin booking.create yapabilmesi için aktif + verified lisans zorunlu',
  scope: {
    subVerticalKeys: ['vet', 'televet', 'mobile_vet'],
    actions: ['booking.create', 'prescription.create'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'compliance_check',
      description: 'Profile compliance vet_license içermeli',
      expression: {
        op: 'contains',
        left: { ref: 'profile.compliance.requiredCertifications' },
        right: 'vet_license',
      },
    },
    {
      id: 'c2',
      type: 'state_check',
      description: 'Lisans süresi geçerli ve verified olmalı',
      expression: {
        op: 'and',
        args: [
          {
            op: '>',
            left: { query: 'vet_licenses.expires_at' },
            right: { fn: 'now' },
          },
          { op: '==', left: { query: 'vet_licenses.verified' }, right: true },
        ],
      },
    },
  ],
  combinator: 'all',
  priority: 100,
  onDeny: {
    statusCode: 403,
    errorCode: 'VET_LICENSE_INVALID',
    messageKey: 'error.vet_license_invalid',
    message: 'Veteriner lisansınız geçersiz veya süresi dolmuş. Lütfen belgenizi güncelleyin.',
  },
  onAllow: { audit: false },
  audit: { level: 'L2', severity: 'warning', logDenial: true, logAllow: false },
  metadata: {
    createdBy: 'cto@petota.com',
    tags: ['compliance', 'vet', 'regulatory'],
    relatedRegulation: 'TR Veteriner Hekimler Odası Kuralları',
  },
  status: 'active',
};

/**
 * 2. POL-CUSTOMER-BLACKLIST
 */
const POL_CUSTOMER_BLACKLIST: PolicyDocument = {
  policyId: 'POL-CUSTOMER-BLACKLIST',
  version: '1.0.0',
  name: 'Müşteri blacklist kontrolü',
  description: 'Blacklist\'teki müşteriden booking/ödeme alınamaz.',
  scope: {
    subVerticalKeys: [],
    actions: ['booking.create', 'payment.create', 'payment.capture'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'state_check',
      description: 'Müşteri blacklist\'te olmamalı',
      expression: {
        op: '==',
        left: { query: 'customer_blacklist.status' },
        right: 'not_listed',
      },
    },
  ],
  combinator: 'all',
  priority: 50,
  onDeny: {
    statusCode: 403,
    errorCode: 'CUSTOMER_BLACKLISTED',
    message: 'Bu müşteri sistem tarafından engellenmiştir.',
  },
  audit: { level: 'L2', severity: 'critical', logDenial: true, logAllow: false },
  metadata: { tags: ['anti-fraud', 'trust'] },
  status: 'active',
};

/**
 * 3. POL-GDPR-RETENTION
 */
const POL_GDPR_RETENTION: PolicyDocument = {
  policyId: 'POL-GDPR-RETENTION',
  version: '1.1.0',
  name: 'KVKK veri saklama süresi',
  description: 'KVKK data retention süresini aşan kayıtlar otomatik anonymize edilir.',
  scope: {
    subVerticalKeys: [],
    actions: ['customer.delete_data', 'customer.read_archived'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'time_check',
      description: 'Kayıt yaşı retention süresinden az olmalı',
      expression: {
        op: '<',
        left: {
          fn: 'business_day_count',
          args: [{ ref: 'customer.createdAt' }, { fn: 'now' }],
        },
        right: { ref: 'profile.compliance.dataRetentionDays' },
      },
    },
  ],
  combinator: 'all',
  priority: 80,
  onDeny: {
    statusCode: 410,
    errorCode: 'DATA_RETENTION_EXPIRED',
    message: 'Bu kayıt yasal saklama süresini aştığı için anonymize edilmiştir.',
  },
  audit: { level: 'L3', severity: 'info', logDenial: true, logAllow: true },
  metadata: { tags: ['compliance', 'kvkk', 'gdpr'], relatedRegulation: 'KVKK Madde 7' },
  status: 'active',
};

/**
 * 4. POL-TENANT-TIER
 */
const POL_TENANT_TIER: PolicyDocument = {
  policyId: 'POL-TENANT-TIER',
  version: '1.0.0',
  name: 'Subscription tier modül kontrolü',
  description: 'Module aktivasyonu tenant tier (Basic/Pro/Enterprise) ile uyumlu olmalı.',
  scope: {
    subVerticalKeys: [],
    actions: ['module.activate', 'module.upgrade'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'tier_check',
      description: 'Module minimum tier ≤ tenant tier',
      expression: {
        op: 'in',
        left: { ref: 'module.minTier' },
        right: { fn: 'tier_tier_or_higher', args: [{ fn: 'tenant_tier' }] },
      },
    },
  ],
  combinator: 'all',
  priority: 90,
  onDeny: {
    statusCode: 402,
    errorCode: 'TIER_UPGRADE_REQUIRED',
    message: 'Bu modül mevcut aboneliğinizde yer almıyor. Lütfen planınızı yükseltin.',
  },
  audit: { level: 'L1', severity: 'info', logDenial: true, logAllow: false },
  metadata: { tags: ['billing', 'subscription'] },
  status: 'active',
};

/**
 * 5. POL-CANCEL-WINDOW
 */
const POL_CANCEL_WINDOW: PolicyDocument = {
  policyId: 'POL-CANCEL-WINDOW',
  version: '1.0.0',
  name: 'İptal penceresi kontrolü',
  description: 'Tenant-tanımlı iptal penceresi dışında iade yok.',
  scope: {
    subVerticalKeys: [],
    actions: ['booking.cancel', 'refund.request'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'time_check',
      description: 'İptal talebi pencerenin içinde olmalı',
      expression: {
        op: 'before',
        left: { fn: 'now' },
        right: {
          fn: 'hours_before',
          args: [
            { ref: 'booking.startAt' },
            { ref: 'profile.settings.cancellationWindowHours' },
          ],
        },
      },
    },
  ],
  combinator: 'all',
  priority: 70,
  onDeny: {
    statusCode: 409,
    errorCode: 'CANCEL_WINDOW_EXPIRED',
    message: 'İptal için tanımlı süre geçmiş. Lütfen işletmeyle iletişime geçin.',
  },
  audit: { level: 'L2', severity: 'info', logDenial: true, logAllow: false },
  metadata: { tags: ['booking', 'refund'] },
  status: 'active',
};

/**
 * 6. POL-MAX-DAILY-BOOKINGS
 */
const POL_MAX_DAILY_BOOKINGS: PolicyDocument = {
  policyId: 'POL-MAX-DAILY-BOOKINGS',
  version: '1.0.0',
  name: 'Günlük max booking limiti',
  description: 'Groomer günlük max booking sayısı aşılmasın.',
  scope: {
    subVerticalKeys: ['groomer_salon', 'groomer_mobile', 'groomer_home'],
    actions: ['booking.create'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'count_check',
      description: 'O günün rezervasyon sayısı limit altında olmalı',
      expression: {
        op: '<',
        left: { query: 'bookings.count_today' },
        right: { ref: 'profile.scheduling.maxDailyBookings' },
      },
    },
  ],
  combinator: 'all',
  priority: 60,
  onDeny: {
    statusCode: 409,
    errorCode: 'DAILY_BOOKING_LIMIT_REACHED',
    message: 'Bu güne ait rezervasyon kapasitesi doldu.',
  },
  audit: { level: 'L1', severity: 'info', logDenial: true, logAllow: false },
  metadata: { tags: ['capacity', 'grooming'] },
  status: 'shadow',
};

/**
 * 7. POL-PRESCRIPTION-REPEAT
 */
const POL_PRESCRIPTION_REPEAT: PolicyDocument = {
  policyId: 'POL-PRESCRIPTION-REPEAT',
  version: '1.0.0',
  name: 'Aynı ilaç tekrar reçetesi sınırı',
  description: 'Aynı ilacın 30 gün içinde tekrar reçetesi için veteriner onayı gerekir.',
  scope: {
    subVerticalKeys: ['vet', 'televet', 'mobile_vet'],
    actions: ['prescription.create'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'count_check',
      description: 'Son 30 günde aynı ilacın reçetesi max 1 olmalı',
      expression: {
        op: '<=',
        left: { query: 'prescriptions.count_same_drug_30d' },
        right: 1,
      },
    },
  ],
  combinator: 'all',
  priority: 75,
  onDeny: {
    statusCode: 409,
    errorCode: 'PRESCRIPTION_REPEAT_LIMIT',
    message: 'Bu ilaç için son 30 gün içinde reçete yazılmış. Veteriner onayı gereklidir.',
  },
  audit: { level: 'L2', severity: 'warning', logDenial: true, logAllow: false },
  metadata: { tags: ['vet', 'pharmacy', 'safety'] },
  status: 'draft',
};

/**
 * 8. POL-FINANCIAL-LIMIT
 */
const POL_FINANCIAL_LIMIT: PolicyDocument = {
  policyId: 'POL-FINANCIAL-LIMIT',
  version: '1.0.0',
  name: 'Günlük payout limiti (anti-fraud)',
  description: 'Tenant başına günlük max payout miktarını aşan talep otomatik bekletme.',
  scope: {
    subVerticalKeys: [],
    actions: ['payout.request', 'payout.approve'],
    applyTo: 'all_tenants',
  },
  conditions: [
    {
      id: 'c1',
      type: 'amount_check',
      description: 'Günlük toplam payout ≤ tenant tier limiti',
      expression: {
        op: '<=',
        left: { query: 'payouts.today_total' },
        right: { fn: 'tenant_daily_payout_limit' },
      },
    },
  ],
  combinator: 'all',
  priority: 40,
  onDeny: {
    statusCode: 423,
    errorCode: 'DAILY_PAYOUT_LIMIT_EXCEEDED',
    message: 'Günlük payout limiti aşıldı. Manuel onay gerekli.',
  },
  audit: { level: 'L2', severity: 'critical', logDenial: true, logAllow: true },
  metadata: { tags: ['anti-fraud', 'financial'] },
  status: 'active',
};

export const SEED_POLICIES: PolicyRecord[] = [
  buildRecord('pol-rec-001', POL_VET_001, {
    activatedAt: iso(-20 * DAYS),
    updatedAt: iso(-2 * DAYS),
  }),
  buildRecord('pol-rec-002', POL_CUSTOMER_BLACKLIST, {
    activatedAt: iso(-25 * DAYS),
    updatedAt: iso(-10 * DAYS),
  }),
  buildRecord('pol-rec-003', POL_GDPR_RETENTION, {
    activatedAt: iso(-28 * DAYS),
    updatedAt: iso(-4 * DAYS),
  }),
  buildRecord('pol-rec-004', POL_TENANT_TIER, {
    activatedAt: iso(-22 * DAYS),
    updatedAt: iso(-6 * DAYS),
  }),
  buildRecord('pol-rec-005', POL_CANCEL_WINDOW, {
    activatedAt: iso(-18 * DAYS),
    updatedAt: iso(-1 * DAYS),
  }),
  buildRecord('pol-rec-006', POL_MAX_DAILY_BOOKINGS, {
    status: 'shadow',
    updatedAt: iso(-2 * DAYS),
  }),
  buildRecord('pol-rec-007', POL_PRESCRIPTION_REPEAT, {
    status: 'draft',
    updatedAt: iso(-3 * HOURS),
  }),
  buildRecord('pol-rec-008', POL_FINANCIAL_LIMIT, {
    activatedAt: iso(-15 * DAYS),
    updatedAt: iso(-7 * DAYS),
  }),
];

export const SEED_EVALUATIONS: Record<string, PolicyEvaluationRecord[]> = {
  'POL-VET-001': [
    {
      id: 'eval-v-001',
      policyId: 'POL-VET-001',
      tenantId: 'ten-aurora-vet',
      tenantName: 'Aurora Vet Clinic',
      userId: 'usr-vet-01',
      action: 'booking.create',
      result: 'allow',
      durationMs: 4,
      correlationId: 'corr-v-001',
      evaluatedAt: iso(-2 * HOURS),
    },
    {
      id: 'eval-v-002',
      policyId: 'POL-VET-001',
      tenantId: 'ten-parkway-vet',
      tenantName: 'Parkway Vet',
      userId: 'usr-vet-02',
      action: 'prescription.create',
      result: 'deny',
      errorMessage: 'VET_LICENSE_INVALID: license expired 2026-03-01',
      durationMs: 6,
      correlationId: 'corr-v-002',
      evaluatedAt: iso(-5 * HOURS),
    },
  ],
  'POL-CUSTOMER-BLACKLIST': [
    {
      id: 'eval-b-001',
      policyId: 'POL-CUSTOMER-BLACKLIST',
      tenantId: 'ten-aurora-vet',
      tenantName: 'Aurora Vet Clinic',
      userId: 'usr-front-01',
      action: 'payment.create',
      result: 'deny',
      errorMessage: 'CUSTOMER_BLACKLISTED: customer blacklisted since 2026-02-01',
      durationMs: 3,
      correlationId: 'corr-b-001',
      evaluatedAt: iso(-10 * HOURS),
    },
  ],
  'POL-FINANCIAL-LIMIT': [
    {
      id: 'eval-f-001',
      policyId: 'POL-FINANCIAL-LIMIT',
      tenantId: 'ten-mutbucket',
      tenantName: 'MutBucket Grooming Co.',
      userId: 'usr-owner-03',
      action: 'payout.request',
      result: 'deny',
      errorMessage: 'DAILY_PAYOUT_LIMIT_EXCEEDED: requested 85000 TRY, limit 50000 TRY',
      durationMs: 9,
      correlationId: 'corr-f-001',
      evaluatedAt: iso(-1 * DAYS),
    },
  ],
};

export const SEED_OVERRIDES: Record<string, PolicyTenantOverride[]> = {
  'POL-VET-001': [
    {
      id: 'ov-v-001',
      tenantId: 'ten-enterprise-alpha',
      tenantName: 'Alpha Pet Enterprise',
      policyId: 'POL-VET-001',
      override: 'force_allow',
      reason:
        'Legacy enterprise partner — legal entity verification covers license renewal out-of-band.',
      createdAt: iso(-14 * DAYS),
      createdBy: 'cto@petota.com',
      expiresAt: iso(60 * DAYS),
    },
  ],
  'POL-MAX-DAILY-BOOKINGS': [],
};
